import React, { useState, useEffect } from 'react';
import { Pause, Volume2, Filter, ChevronDown, X } from 'lucide-react';

const VoiceSelector = ({
  voices,
  selectedVoice,
  onVoiceSelect,
  onPreviewPlay,
  currentlyPlaying
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [filteredVoices, setFilteredVoices] = useState(voices);
  const [filters, setFilters] = useState({
    gender: '',
    age: ''
  });
  
  // Count of active filters for badge
  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  // Apply filters when they change
  useEffect(() => {
    let result = [...voices];
    
    if (filters.gender) {
      result = result.filter(voice => {
        // Use Gender property with exact match, not partial match
        if (voice.Gender && typeof voice.Gender === 'string') {
          // Case insensitive exact match for gender
          return voice.Gender.toLowerCase() === filters.gender.toLowerCase();
        }
        
        // For description, use more precise matching to avoid partial matches
        const description = (voice.description || voice.Description || '').toLowerCase();
        
        // Check for more precise gender patterns to avoid matching "female" when searching for "male"
        if (filters.gender.toLowerCase() === 'male') {
          // Match "male" but not as part of "female"
          return /\bmale\b/i.test(description) && !/\bfemale\b/i.test(description);
        } else if (filters.gender.toLowerCase() === 'female') {
          return /\bfemale\b/i.test(description);
        }
        
        return false;
      });
    }
    
    if (filters.age) {
      result = result.filter(voice => {
        // Use Age property first if available
        if (voice.Age && typeof voice.Age === 'string') {
          return voice.Age.toLowerCase() === filters.age.toLowerCase();
        }
        // Fallback to description search
        const description = (voice.description || voice.Description || '').toLowerCase();
        return description.includes(filters.age.toLowerCase());
      });
    }
    
    setFilteredVoices(result);
  }, [voices, filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const toggleFilters = () => {
    setShowFilters(prev => !prev);
  };
  
  const resetFilters = () => {
    setFilters({ gender: '', age: '' });
  };

  // Safely get voice name
  const getVoiceName = (voice) => {
    return voice.name || voice['Display Name'] || `Voice ${voice.id}`;
  };
  
  // Safely get voice description
  const getVoiceDescription = (voice) => {
    return voice.description || voice.Description || '';
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-[clamp(0.25rem,0.5vw,0.5rem)]">
        <div className="flex justify-between items-center">
          <label className="block text-[clamp(0.875rem,1.5vw,1.125rem)] text-[#B0B4BB] font-bold">
            Select Voice Profile
          </label>
          <div className="relative">
            <button 
              onClick={toggleFilters}
              className={`p-1.5 rounded-full hover:bg-[#2C2F30] transition-colors ${showFilters ? 'bg-[#2C2F30] text-[#178A94]' : 'text-[#B0B4BB]'}`}
              aria-label="Toggle filters"
            >
              <Filter size={16} />
            </button>
            
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#178A94] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </div>
        </div>
        
        {showFilters && (
          <div className="mt-2 p-2 bg-[#181A1B] rounded-lg border border-[#2C2F30]">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <select
                    value={filters.gender}
                    onChange={(e) => handleFilterChange('gender', e.target.value)}
                    className="w-full bg-[#2C2F30] text-[#EAECEF] py-1 px-2 pr-6 rounded text-sm appearance-none border border-[#3A3D3E] focus:border-[#178A94] focus:outline-none"
                  >
                    <option value="">Gender: All</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-1 pointer-events-none text-[#B0B4BB]">
                    <ChevronDown size={14} />
                  </div>
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <select
                    value={filters.age}
                    onChange={(e) => handleFilterChange('age', e.target.value)}
                    className="w-full bg-[#2C2F30] text-[#EAECEF] py-1 px-2 pr-6 rounded text-sm appearance-none border border-[#3A3D3E] focus:border-[#178A94] focus:outline-none"
                  >
                    <option value="">Age: All</option>
                    <option value="young">Young</option>
                    <option value="middle aged">Middle Aged</option>
                    <option value="old">Old</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-1 pointer-events-none text-[#B0B4BB]">
                    <ChevronDown size={14} />
                  </div>
                </div>
              </div>

              {/* Reset Filters button */}
              {activeFiltersCount > 0 && (
                <button
                  onClick={resetFilters}
                  className="flex items-center justify-center h-7 w-7 text-[#178A94] hover:bg-[#2C2F30] rounded transition-colors"
                  aria-label="Reset filters"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div className="flex-1 space-y-[clamp(0.25rem,1vw,0.5rem)] overflow-y-auto pr-[0.25rem] scrollbar-thin scrollbar-thumb-[#178A94] scrollbar-track-[#2C2F30]">
        {filteredVoices.length > 0 ? (
          filteredVoices.map((v) => (
            <div
              key={v.id}
              className={`flex items-center p-[clamp(0.5rem,1.5vw,0.75rem)] rounded-lg ${
                selectedVoice === v.id ? 'bg-[#2C2F30] border border-[#178A94]'
                 : 'bg-[#181A1B]'
              } hover:bg-[#3A3D3E] transition-colors`}
            >
              <div
                className="flex-grow cursor-pointer"
                onClick={() => onVoiceSelect(v.id)}
              >
                <div className="font-bold text-[clamp(0.875rem,1.5vw,1rem)] text-[#EAECEF] flex items-center flex-wrap">
                  {getVoiceName(v)}
                  {v.tags && v.tags.length > 0 && (
                    <div className="ml-2 flex gap-1 flex-wrap">
                      {v.tags.map((tag, index) => (
                        <span
                           key={index}
                           className="text-[clamp(0.65rem,1vw,0.75rem)] bg-[#178A94] text-white px-1.5 py-0.5 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-[clamp(0.75rem,1.25vw,0.875rem)] text-[#B0B4BB]">
                  {getVoiceDescription(v)}
                </div>
              </div>
              <button
                onClick={() => onPreviewPlay(v.id, v.previewUrl || v.preview_link, getVoiceName(v))}
                className="ml-[clamp(0.25rem,1vw,0.5rem)] p-[clamp(0.5rem,1vw,0.75rem)] bg-[#178A94] text-[#EAECEF] rounded-full hover:bg-[#146E75] transition-colors"
              >
                {currentlyPlaying === v.id ? <Pause size={16} /> : <Volume2 size={16} />}
              </button>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-[#B0B4BB]">
            No voices match the selected filters
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceSelector;